import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Link,
    Alert,
    IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { FormTextField } from '../components/common/FormFields';
import PhoneInput from '../components/common/PhoneInput';
import CountrySelect from '../components/common/CountrySelect';
import { countryStateConfig, ageRanges, genderOptions, OptionType } from '../services/utils/lists';
import { malaysiaDistricts } from '../constants/district';
import { registerSchema } from '../services/utils/validation';
import { useAuth } from '../hooks/useAuth';
import type { RegisterFormValues } from '../services/types/auth';
import PublicLayout from '../layouts/PublicLayout';

const Register: React.FC = () => {
    const { t } = useTranslation();
    const [openTerms, setOpenTerms] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const handleOpenTerms = (event: React.MouseEvent) => {
        event.preventDefault();
        setOpenTerms(true);
    };

    const handleCloseTerms = () => {
        setOpenTerms(false);
    };
    const { register, error: authError } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (values: RegisterFormValues) => {
        try {
            setError(null);
            const { confirmPassword, ...registerData } = values;
            await register(registerData);
            
            // Redirect to verification pending
            navigate('/verification-pending', { 
                state: { 
                    email: values.email,
                    message: t('auth.registerSuccess')
                }
            });

            // BYPASS: Redirect to login instead of verification pending
            // navigate('/login');

        } catch (err: any) {
            setError(err.response?.data?.error || t('auth.registerError'));
            console.error('Registration failed:', err);
        }
    };

    return (
        <PublicLayout>
            <Container component="main" maxWidth="sm">
                <Box
                    sx={{
                        marginTop: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
                        <Typography component="h1" variant="h5" align="center" gutterBottom>
                            {t('auth.createAccount')}
                        </Typography>

                        {(authError || error) && (
                            <Alert
                                severity="error"
                                action={
                                    <IconButton
                                        aria-label="close"
                                        color="inherit"
                                        size="small"
                                        onClick={() => setError(null)}
                                    >
                                        <CloseIcon fontSize="inherit" />
                                    </IconButton>
                                }
                                sx={{ mb: 2 }}
                            >
                                {authError || error}
                            </Alert>
                        )}

                        <Formik
                            initialValues={{
                                email: '',
                                password: '',
                                confirmPassword: '',
                                first_name: '',
                                last_name: '',
                                phone: '',
                                address: '',
                                country: '',
                                state: '',
                                age_range: '',
                                gender: '',
                                school_name: '',
                                school_district: ''
                            }}
                            validationSchema={registerSchema(t)}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting, values, setFieldValue }) => (
                                <Form>
                                    <Grid 
                                        container 
                                        spacing={1}
                                        sx={{ 
                                            '& .MuiGrid-item': {
                                                paddingTop: 0.75,
                                                paddingBottom: 0.75
                                            },
                                            '& .MuiFormControl-root': {
                                                margin: 0
                                            },
                                            '& .MuiInputBase-root': {
                                                margin: 0,
                                                height: '56px'
                                            },
                                            '& .MuiSelect-select': {
                                                height: '23px',
                                                padding: '16.5px 14px'
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                marginTop: 0
                                            },
                                            '& .MuiInputLabel-root': {
                                                transform: 'translate(14px, 16px) scale(1)'
                                            },
                                            '& .MuiInputLabel-shrink': {
                                                transform: 'translate(14px, -9px) scale(0.75)'
                                            }
                                        }}
                                    >
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormTextField
                                                    required
                                                    fullWidth
                                                    id="first_name"
                                                    label={t('auth.firstName')}
                                                    name="first_name"
                                                    autoFocus
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormTextField
                                                    required
                                                    fullWidth
                                                    id="last_name"
                                                    label={t('auth.lastName')}
                                                    name="last_name"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1, mb: 1 }}>
                                                {t('auth.nameNote')}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormControl fullWidth required>
                                                    <InputLabel id="age-range-label">{t('auth.ageRange')}</InputLabel>
                                                    <Select
                                                        labelId="age-range-label"
                                                        id="age_range"
                                                        name="age_range"
                                                        value={values.age_range}
                                                        label={t('auth.ageRange')}
                                                        onChange={(e) => setFieldValue('age_range', e.target.value)}
                                                    >
                                                        <MenuItem value="">
                                                            <em>{t('common.select')} {t('auth.ageRange')}</em>
                                                        </MenuItem>
                                                        {ageRanges.map((range: OptionType) => (
                                                            <MenuItem key={range.value} value={range.value}>
                                                                {range.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormControl fullWidth required>
                                                    <InputLabel id="gender-label">{t('auth.gender')}</InputLabel>
                                                    <Select
                                                        labelId="gender-label"
                                                        id="gender"
                                                        name="gender"
                                                        value={values.gender}
                                                        label={t('auth.gender')}
                                                        onChange={(e) => setFieldValue('gender', e.target.value)}
                                                    >
                                                        <MenuItem value="">
                                                            <em>{t('common.select')} {t('auth.gender')}</em>
                                                        </MenuItem>
                                                        {genderOptions.map((option: OptionType) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormTextField
                                                    required
                                                    fullWidth
                                                    id="school_name"
                                                    label={t('auth.schoolName')}
                                                    name="school_name"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormControl fullWidth required>
    <InputLabel id="school-district-label">{t('auth.schoolDistrict')}</InputLabel>
    <Select
        labelId="school-district-label"
        id="school_district"
        name="school_district"
        value={values.school_district}
        label={t('auth.schoolDistrict')}
        onChange={(e) => setFieldValue('school_district', e.target.value)}
        MenuProps={{ style: { maxHeight: 400 } }}
    >
        <MenuItem value="">
            <em>{t('common.select')} {t('auth.schoolDistrict')}</em>
        </MenuItem>
        {malaysiaDistricts.map((district) => (
            <MenuItem key={district} value={district}>
                {district}
            </MenuItem>
        ))}
    </Select>
</FormControl>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormTextField
                                                    required
                                                    fullWidth
                                                    id="address"
                                                    label={t('auth.address')}
                                                    name="address"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormControl fullWidth required>
                                                    <InputLabel required>{t('auth.country')}</InputLabel>
                                                    <Select
                                                        name="country"
                                                        value={values.country || ''}
                                                        label={t('auth.country')}
                                                        onChange={(e) => {
                                                            setFieldValue('country', e.target.value);
                                                            setFieldValue('state', '');
                                                        }}
                                                    >
                                                        <MenuItem value="">
                                                            <em>{t('common.select')} {t('auth.country')}</em>
                                                        </MenuItem>
                                                        {Object.keys(countryStateConfig).map((country) => (
                                                            <MenuItem key={country} value={country}>
                                                                {country}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormControl fullWidth required>
                                                    <InputLabel required>{t('auth.state')}</InputLabel>
                                                    <Select
                                                        name="state"
                                                        value={values.state || ''}
                                                        label={t('auth.state')}
                                                        onChange={(e) => setFieldValue('state', e.target.value)}
                                                    >
                                                        <MenuItem value="">
                                                            <em>{t('common.select')} {t('auth.state')}</em>
                                                        </MenuItem>
                                                        {values.country && countryStateConfig[values.country]?.states.map((state) => (
                                                            <MenuItem key={state} value={state}>
                                                                {state}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <PhoneInput
                                                    required
                                                    fullWidth
                                                    id="phone"
                                                    label={t('auth.phone')}
                                                    name="phone"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sx={{ mb: 2 }}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormTextField
                                                    required
                                                    fullWidth
                                                    id="email"
                                                    label={t('auth.email')}
                                                    name="email"
                                                    type="email"
                                                    autoComplete="email"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormTextField
                                                    required
                                                    fullWidth
                                                    name="password"
                                                    label={t('auth.password')}
                                                    type="password"
                                                    id="password"
                                                    autoComplete="new-password"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ height: '100%', '& .MuiFormControl-root': { margin: 0 } }}>
                                                <FormTextField
                                                    required
                                                    fullWidth
                                                    name="confirmPassword"
                                                    label={t('auth.confirmPassword')}
                                                    type="password"
                                                    id="confirmPassword"
                                                    autoComplete="new-password"
                                                />
                                            </Box>
                                        </Grid>

                                    </Grid>

                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={acceptedTerms}
                                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                                    <Typography variant="body2" component="span">
                                                        {t('auth.acceptTerms')} {' '}
                                                        <Link href="#" onClick={handleOpenTerms} sx={{ cursor: 'pointer' }}>
                                                            {t('auth.termsAndConditions')}
                                                        </Link>
                                                        *
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </Box>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        sx={{ mb: 2 }}
                                        disabled={isSubmitting || !acceptedTerms || !(values.email && 
                                            values.password && 
                                            values.confirmPassword && 
                                            values.first_name && 
                                            values.last_name && 
                                            values.phone && 
                                            values.age_range && 
                                            values.gender && 
                                            values.country && 
                                            values.state && 
                                            values.school_name && 
                                            values.school_district)}
                                    >
                                        {isSubmitting ? t('common.loading') : t('common.register')}
                                    </Button>

                                    <Dialog
                                        open={openTerms}
                                        onClose={handleCloseTerms}
                                        aria-labelledby="terms-dialog-title"
                                        aria-describedby="terms-dialog-description"
                                        maxWidth="md"
                                        fullWidth
                                    >
                                        <DialogTitle id="terms-dialog-title">
                                            {t('auth.termsAndConditions')}
                                        </DialogTitle>
                                        <DialogContent>
                                            <DialogContentText id="terms-dialog-description">
                                                <Typography paragraph>
                                                    {t('auth.termsAndConditionsText')}
                                                </Typography>
                                            </DialogContentText>
                                        </DialogContent>
                                        <DialogActions>
                                            <Button onClick={handleCloseTerms} color="primary">
                                                {t('common.cancel')}
                                            </Button>
                                        </DialogActions>
                                    </Dialog>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Link href="/login" variant="body2">
                                            {t('auth.haveAccount')} {t('auth.signIn')}
                                        </Link>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    </Paper>
                </Box>
            </Container>
        </PublicLayout>
    );
};

export default Register;
